import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cpu, Brain, Zap, Network, ChevronRight } from 'lucide-react';
import { expertiseAreas } from '../data/mock';

const iconMap = {
  Chip: Cpu,
  Brain: Brain,
  Zap: Zap,
  Network: Network
};

const Expertise = () => {
  const { t } = useTranslation();
  return (
    <section id="expertise" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Technical Background Layers */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-40" />
      <div className="absolute inset-0 bg-hex-pattern" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-teal-100/50 to-transparent rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-100/50 to-transparent rounded-full blur-3xl animate-pulse-slower" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('expertise.title')} <span className="bg-gradient-to-r from-cyan-700 to-teal-700 bg-clip-text text-transparent">{t('expertise.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed font-medium">
            {t('expertise.subtitle')}
          </p>
        </div>

        {/* Expertise Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {expertiseAreas.map((area, index) => {
            const Icon = iconMap[area.icon];
            // Map background images to each expertise area
            const backgroundImages = [
              'https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/sy0d7dej_IMG_8697.JPG', // Semiconductors
              'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80', // AI & ML
              'https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/idcchfcm_IMG_8701.JPG', // Power Electronics
              'https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/t12dxx9h_IMG_8698.JPG' // Future Grid
            ];
            return (
              <div 
                key={area.id}
                className="group p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-cyan-600 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Background Image at 20% opacity */}
                <div 
                  className="absolute inset-0 bg-cover bg-center z-0"
                  style={{ 
                    backgroundImage: `url(${backgroundImages[index]})`,
                    opacity: 0.20
                  }}
                />
                
                {/* Tech Corner Decorations */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-cyan-300/50 group-hover:border-cyan-500 transition-colors" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-cyan-300/50 group-hover:border-cyan-500 transition-colors" />
                
                {/* Gradient Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-teal-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 group-hover:text-cyan-800 transition-colors">
                          {area.title}
                        </h3>
                        <p className="text-sm text-slate-700 mt-1 font-medium">{area.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Specializations */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-cyan-800 uppercase tracking-wide mb-4">
                      {t('expertise.keySpecializations')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {area.specializations.map((spec, specIndex) => (
                        <div 
                          key={specIndex}
                          className="flex items-start space-x-2 text-slate-800 hover:text-cyan-800 transition-colors group/item"
                        >
                          <ChevronRight className="w-4 h-4 mt-0.5 text-cyan-600 flex-shrink-0 group-hover/item:translate-x-1 transition-transform" />
                          <span className="text-sm leading-relaxed font-medium">{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-700 font-medium mb-6">
            {t('expertise.bottomCTA')}
          </p>
          <a 
            href="#contact"
            className="inline-flex items-center text-cyan-800 font-bold hover:text-cyan-700 transition-colors group text-lg"
          >
            {t('expertise.bottomLink')}
            <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Expertise;
